import { z } from 'zod';

import { api } from '@/shared/api';
import { unwrapCollection } from '@/shared/api/envelope';

import {
    toAddresses,
    toApiKey,
    toApiKeyActivity,
    type ApiKey,
    type ApiKeyActivity,
    type ApiKeyList,
    type IssuedApiKey,
    type NewApiKeyInput,
} from './model';
import {
    apiKeyActivityResourceSchema,
    apiKeyListSchema,
    apiKeyResourceSchema,
    createdApiKeySchema,
} from './types';

/** Schrijfacties gaan nooit in de offline cache. */
const WRITE = { cacheKey: false } as const;

/** Sleutels zijn geheim spul: ook de lijst niet in de offline cache. */
const READ = { cacheKey: false } as const;

/**
 * De API-sleutels van één plek: het eigen bedrijf (`/api-keys`, Instellingen →
 * Koppelingen) of het platform (`/platform/api-keys`, voor de beheer-MCP). Dezelfde
 * vorm, een ander pad.
 */
export interface ApiKeysApi {
    list(): Promise<ApiKeyList>;
    create(input: NewApiKeyInput): Promise<IssuedApiKey>;
    revoke(uuid: string): Promise<ApiKey>;
    activity(uuid: string): Promise<ApiKeyActivity[]>;
}

function apiKeysAt(base: '/api-keys' | '/platform/api-keys'): ApiKeysApi {
    return {
        async list() {
            const payload = apiKeyListSchema.parse(await api.get(base, READ));

            return { keys: payload.data.map(toApiKey), addresses: toAddresses(payload.meta) };
        },

        async create(input) {
            const body: Record<string, unknown> = {
                name: input.name.trim(),
                expires_at: input.expiresOn,
            };

            if (base === '/api-keys') {
                body.scopes = [...input.scopes];
            }

            const payload = createdApiKeySchema.parse(await api.post(base, body, WRITE));

            return {
                key: toApiKey(payload.data),
                secret: payload.data.key ?? '',
                addresses: toAddresses(payload.meta),
            };
        },

        async revoke(uuid) {
            const payload = z
                .object({ data: apiKeyResourceSchema })
                .parse(await api.delete(`${base}/${uuid}`, WRITE));

            return toApiKey(payload.data);
        },

        async activity(uuid) {
            return unwrapCollection(
                apiKeyActivityResourceSchema,
                await api.get(`${base}/${uuid}/activity`, READ),
            ).items.map(toApiKeyActivity);
        },
    };
}

export const companyApiKeysApi = apiKeysAt('/api-keys');

export const platformApiKeysApi = apiKeysAt('/platform/api-keys');
